import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  INVOICE_STATUSES,
  type InvoiceStatus,
  type Project,
  type ProjectDashboard,
  type VendorCostSummary,
} from '@faktorplus/shared';
import { useArchiveProject, useProjectDashboard, useUnarchiveProject } from '../../api/projects';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { Modal, modalStyles } from '../../components/ui/modal';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { ApiError } from '../../lib/api-client';
import {
  formatDate,
  formatDayCount,
  formatMoney,
  formatMoneyCompact,
  plural,
} from '../../lib/format';
import {
  BudgetMeter,
  MonthlyCostChart,
  RankedCostBars,
  StatusPipeline,
  type RankedItem,
} from './project-charts';
import { PeriodFilter } from './period-filter';
import { ProjectDocuments } from './project-documents';
import { ProjectInvoices } from './project-invoices';
import { ProjectOrders } from './project-orders';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

/** Past this many vendors the bars stop being readable and start being a table. */
const VENDOR_LIMIT = 8;

/**
 * One site, from the top: what it has cost, where that money went, how it is
 * trending, and which invoices are behind the numbers.
 *
 * The period filter drives the whole screen — tiles, charts and list alike —
 * so a number and the rows under it always describe the same window. The
 * status filter sits on the list itself, because narrowing to "disputed" is a
 * question about documents, not about cost.
 */
export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const period = readPeriod(searchParams);
  const { data, isLoading, error } = useProjectDashboard(id, period);

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Any change to what is being asked invalidates the current page number.
    if (!('page' in patch)) next.delete('page');

    setSearchParams(next, { replace: true });
  }

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorBox>{describeError(error)}</ErrorBox>;

  const { project, totals, budget } = data;

  return (
    <>
      <header className={pageStyles.header}>
        <Link
          to={project.archivedAt ? '/budowy/archiwum' : '/budowy'}
          className={styles.backLink}
        >
          {project.archivedAt ? '← Archiwum budów' : '← Budowy'}
        </Link>
        <h1 className={pageStyles.title}>{project.name}</h1>
        <span className={pageStyles.subtitle}>
          {project.code}
          {!project.isActive && !project.archivedAt && ' · zakończona'}
        </span>
        <ArchiveActions project={project} />
      </header>

      {project.archivedAt && (
        <div className={`${pageStyles.banner} ${pageStyles.bannerInfo}`} role="status">
          <span aria-hidden="true">🗄</span>
          <span>
            <b>Budowa w archiwum od {formatDate(project.archivedAt)}.</b> Faktury, dokumenty
            i zdjęcia zostają tu w komplecie; w rejestrze i na listach już jej nie ma.
          </span>
        </div>
      )}

      <PeriodFilter from={period.from} to={period.to} onChange={update} />

      <StatTiles>
        <StatTile
          label="Koszty netto"
          value={formatMoneyCompact(totals.netAmount)}
          note={`${totals.invoiceCount} ${plural(totals.invoiceCount, ['faktura', 'faktury', 'faktur'])} · brutto ${formatMoneyCompact(totals.grossAmount)}`}
        />

        <StatTile
          label="Do zapłaty"
          value={formatMoneyCompact(totals.unpaid.amount)}
          note={`${totals.unpaid.count} ${plural(totals.unpaid.count, ['niezapłacona', 'niezapłacone', 'niezapłaconych'])}`}
        />

        <StatTile
          tone={totals.overdue.count > 0 ? 'critical' : 'default'}
          label="Po terminie"
          value={String(totals.overdue.count)}
          note={
            totals.overdue.count > 0
              ? `${formatMoney(totals.overdue.amount)} · ${formatDayCount(totals.overdue.maxDaysLate)}`
              : 'wszystko w terminie'
          }
        />

        <StatTile
          tone={totals.awaitingManager.count > 0 ? 'warning' : 'default'}
          label="Czeka na kierownika"
          value={String(totals.awaitingManager.count)}
          note={totals.awaitingManager.count > 0 ? 'do akceptacji' : 'nic nie czeka'}
        />
      </StatTiles>

      <div className={styles.panels}>
        <Panel title="Koszty netto w czasie">
          {data.byMonth.length === 0 ? (
            <EmptyPanel />
          ) : (
            <MonthlyCostChart points={data.byMonth} />
          )}
        </Panel>

        <div className={styles.panelRow}>
          <Panel title="Na co idą pieniądze">
            {data.byCategory.length === 0 ? (
              <EmptyPanel />
            ) : (
              <RankedCostBars items={categoryItems(data)} total={totals.netAmount} />
            )}
          </Panel>

          <Panel title="Najwięksi kontrahenci">
            {data.byVendor.length === 0 ? (
              <EmptyPanel />
            ) : (
              <RankedCostBars items={vendorItems(data.byVendor)} total={totals.netAmount} />
            )}
          </Panel>
        </div>

        <div className={styles.panelRow}>
          {/* Deliberately outside the period filter — see the API's `budget`. */}
          <Panel title="Wykorzystanie kontraktu — narastająco">
            {budget ? (
              <BudgetMeter budget={budget} />
            ) : (
              <p className={pageStyles.muted}>
                Ta budowa nie ma zapisanej wartości kontraktu, więc nie ma czego wykorzystywać.
              </p>
            )}
          </Panel>

          <Panel title="Gdzie stoją faktury">
            {data.byStatus.length === 0 ? <EmptyPanel /> : <StatusPipeline entries={data.byStatus} />}
          </Panel>
        </div>

        <ProjectInvoices
          projectId={project.id}
          filters={{ ...period, status: readStatus(searchParams), page: readPage(searchParams) }}
          onChange={update}
        />

        <ProjectOrders projectId={project.id} from={period.from} to={period.to} />

        {/* Outside the period filter on purpose: the folder is the site's
            whole paper trail, not a window into a quarter. */}
        <ProjectDocuments projectId={project.id} />
      </div>
    </>
  );
}

function EmptyPanel() {
  return <p className={pageStyles.muted}>Brak danych za wybrany okres.</p>;
}

/**
 * Closing a site (24.09.2026): "kończy się budowa, klikacie i przenosimy do
 * archiwum" — with a confirmation naming what happens, and the API refusing
 * while anything on the site is still owed. Reversible from the archive.
 */
function ArchiveActions({ project }: { project: Project }) {
  const user = useCurrentUser();
  const [isConfirming, setIsConfirming] = useState(false);
  const archive = useArchiveProject();
  const unarchive = useUnarchiveProject();

  if (user.role === 'SITE_MANAGER') return null;

  if (project.archivedAt) {
    return (
      <div className={pageStyles.actions}>
        {unarchive.error && (
          <ErrorBox>
            {unarchive.error instanceof ApiError
              ? unarchive.error.message
              : 'Nie udało się przywrócić budowy.'}
          </ErrorBox>
        )}
        <Button
          isLoading={unarchive.isPending}
          onClick={() => unarchive.mutate(project.id)}
        >
          Przywróć z archiwum
        </Button>
      </div>
    );
  }

  return (
    <div className={pageStyles.actions}>
      <Button onClick={() => setIsConfirming(true)}>Zakończ i zarchiwizuj</Button>

      {isConfirming && (
        <Modal
          isOpen
          title="Zarchiwizować budowę?"
          onClose={() => setIsConfirming(false)}
          footer={
            <>
              <Button onClick={() => setIsConfirming(false)} disabled={archive.isPending}>
                Anuluj
              </Button>
              <span className={modalStyles.footerSpacer} />
              <Button
                variant="primary"
                isLoading={archive.isPending}
                onClick={() =>
                  void archive.mutateAsync(project.id).then(() => setIsConfirming(false))
                }
              >
                Zarchiwizuj
              </Button>
            </>
          }
        >
          <p>
            Budowa <b>{project.name}</b> przeniesie się do archiwum w całości — z fakturami,
            dokumentami i zdjęciami. Zniknie z list, filtrów i pulpitu; pozostanie dostępna
            w „Archiwum budów" i w każdej chwili można ją przywrócić.
          </p>

          {archive.error && (
            <ErrorBox>
              {archive.error instanceof ApiError
                ? archive.error.message
                : 'Nie udało się zarchiwizować budowy.'}
            </ErrorBox>
          )}
        </Modal>
      )}
    </div>
  );
}

function categoryItems(data: ProjectDashboard): RankedItem[] {
  return data.byCategory.map((category) => ({
    key: category.categoryId ?? 'none',
    label: category.categoryName,
    amount: category.netAmount,
    note: `${category.invoiceCount} ${plural(category.invoiceCount, ['faktura', 'faktury', 'faktur'])}`,
  }));
}

/**
 * The tail is folded into one bar rather than dropped — a "top 8" that quietly
 * loses a third of the costs is how a chart starts lying.
 */
function vendorItems(vendors: VendorCostSummary[]): RankedItem[] {
  const top = vendors.slice(0, VENDOR_LIMIT).map((vendor) => ({
    key: vendor.vendorId,
    label: vendor.vendorName,
    amount: vendor.netAmount,
    note: `${vendor.invoiceCount} ${plural(vendor.invoiceCount, ['faktura', 'faktury', 'faktur'])}`,
  }));

  const rest = vendors.slice(VENDOR_LIMIT);
  if (rest.length === 0) return top;

  return [
    ...top,
    {
      key: 'rest',
      label: `Pozostali (${rest.length})`,
      amount: rest.reduce((sum, vendor) => sum + vendor.netAmount, 0),
      note: `${rest.reduce((sum, vendor) => sum + vendor.invoiceCount, 0)} faktur`,
    },
  ];
}

function readPeriod(params: URLSearchParams): { from?: string; to?: string } {
  return {
    from: params.get('from') || undefined,
    to: params.get('to') || undefined,
  };
}

function readStatus(params: URLSearchParams): InvoiceStatus | undefined {
  const status = params.get('status');
  return INVOICE_STATUSES.find((candidate) => candidate === status);
}

function readPage(params: URLSearchParams): number {
  return Number(params.get('page')) || 1;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return 'Nie masz dostępu do tej budowy.';
  }
  return 'Nie udało się pobrać danych budowy.';
}
